/**
 * Program and Audience Type Restrictions
 * Allow user to override the program type
 *
 * These constants define the allowed audience types for each program.
 * When a program is selected, the Audience dropdown is filtered to only show
 * audiences from this mapping.
 */

/**
 * Programs that have specific audience restrictions.
 * Programs not in this list allow all audience types.
 */
export const PROGRAMS_WITH_AUDIENCE_RESTRICTIONS = [
    'Commercial',
    'Partner Program',
    'Dedicated Cloud',
    'Partner Reseller',
    'AWS Authorized Instructor (AAI)',
    'World Wide Field Enablement',
] as const;

/**
 * Audience types allowed for Commercial program
 */
export const COMMERCIAL_ALLOWED_AUDIENCES = [
    'Public',
    'Commercial Private',
    'Commercial Semi-Private',
    'Internal',
] as const;

/**
 * Audience types allowed for Partner Program
 */
export const PARTNER_PROGRAM_ALLOWED_AUDIENCES = [
    'Partner Public',
    'Partner Public Unlisted',
    'Partner Private',
    'Partner Semi-Private',
    'Internal',
] as const;

/**
 * Audience types allowed for Dedicated Cloud program
 */
export const DEDICATED_CLOUD_ALLOWED_AUDIENCES = [
    'Commercial Private',
    'Commercial Semi-Private',
] as const;

/**
 * Audience types allowed for Partner Reseller program
 */
export const PARTNER_RESELLER_ALLOWED_AUDIENCES = [
    'Partner Private',
    'Partner Public Unlisted',
] as const;

/**
 * Audience types allowed for AWS Authorized Instructor (AAI) program
 */
export const AAI_ALLOWED_AUDIENCES = ['Partner Private'] as const;

/**
 * Audience types allowed for World Wide Field Enablement program
 */
export const WWFE_ALLOWED_AUDIENCES = [
    'Partner Private',
    'Partner Public Unlisted',
] as const;

/**
 * Mapping of program names to their allowed audience types.
 * Used to filter the Audience dropdown based on selected program.
 */
export const PROGRAM_AUDIENCE_MAP: Record<string, readonly string[]> = {
    Commercial: COMMERCIAL_ALLOWED_AUDIENCES,
    'Partner Program': PARTNER_PROGRAM_ALLOWED_AUDIENCES,
    'Dedicated Cloud': DEDICATED_CLOUD_ALLOWED_AUDIENCES,
    'Partner Reseller': PARTNER_RESELLER_ALLOWED_AUDIENCES,
    'AWS Authorized Instructor (AAI)': AAI_ALLOWED_AUDIENCES,
    'World Wide Field Enablement': WWFE_ALLOWED_AUDIENCES,
};

/**
 * Programs that support auto-assign instructor feature
 * Used in OpportunityForm.tsx for vILT modality
 */
export const AUTO_ASSIGN_INSTRUCTOR_PROGRAMS = [
    'Commercial',
    'Partner Program',
] as const;

/**
 * Helper function to get allowed audiences for a program
 * Returns undefined if program has no restrictions (allows all audiences)
 */
export const getAllowedAudiencesForProgram = (
    program: string,
): readonly string[] | undefined => {
    return PROGRAM_AUDIENCE_MAP[program];
};

/**
 * Helper function to check if a program has audience restrictions
 */
export const programHasAudienceRestrictions = (program: string): boolean => {
    return (PROGRAMS_WITH_AUDIENCE_RESTRICTIONS as readonly string[]).includes(
        program,
    );
};
